import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {replyMode, replyRecipientSeed, type ReplyTarget} from "download/reply/replyPrompt";
import {useLocale, useMessages} from "i18n";
import {useIsLoggedIn, useUser} from "UserProvider";
import ExpiresAfter from "../../domain/ExpiresAfter";
import {KeyMode} from "../../core/upload/uploadContract";
import {
	canUseDownloadNotifications, canUseTransferOptions, coerceExpiry, defaultExpiry,
	isOverUploadCap,
	expiryOptions, INSTANT_EXPIRY, Mode, nextUpgradePlan, Plan,
	sanitizeMaxDownloads,
} from "../gating";
import {expiryLabelOf} from "../expiryLabel";
import {
	canonicalSenderEmail, declaredSelectionBytes, deliveryOf, isHostedSelectionAboveEveryPlan, resolveZeroKnowledge,
	storedFilesMode,
	isRecipientDelivery, isValidSender, LockedBadge, lockedBadgeFor, LockedTierCopy, OptionKey,
	optionHintFor, overLimitPricingOf, pillLabelFor,
	RecipientSelection, resolveSubmit, SubmitStatus,
	TRANSFER_OPTIONS, usesHostedUpload,
	MAX_TRANSFER_PASSWORD_BYTES, MIN_TRANSFER_PASSWORD_LENGTH, transferPasswordFeedback, transferPasswordIssue,
} from "./createFormLogic";
import {canRecoverExpiredTransfers} from "../../transfers/transferMutations";
import {hostedUploadLimitLabel, maxFilesPerTransfer} from "../../pricing/planEntitlements";
import {planCodeForId, type PaidPlanId} from "../../pricing/planCatalogue";
import {useRecipientsField, type RecipientsFieldVM} from "./useRecipientsField";
import {isPlanMaxExpiry, type UpsellTrigger} from "./upsellCard";
import type {AttachedPersonalization} from "../personalization/personalizationDraft";
import type {SenderBlocker, SenderCompany} from "sender/senderChoice";
import type {ComposerMode} from "../request/composerMode";
import {
	overLimitAllowanceId, overLimitBlocksSend, overLimitMoment, overLimitShowsUpgradeNudge, sheetCloseDismissesWall,
	overLimitSignedOutMoment, overLimitSignedOutReached, overLimitSignedOutSignInOpened, overLimitWallShown, passWallFor,
	planDialogOpensAfterRefusal, preferredOverLimitAllowance, resolveOverLimit,
	sizeWallResolution, storagePassPaid, type OverLimitPlanOffer, type OverLimitPricing, type OverLimitResolution,
} from "../overLimit/overLimitOffer";
import {arrivedSignedOut as arrivedSignedOutIn, rememberSignedOutWall} from "../overLimit/overLimitSignedOutArrival";
import {browserSessionStorage} from "core/browserStorage";
import {useHasMounted} from "utils/useHasMounted";
import {useOverLimitPurchase} from "../overLimit/useOverLimitPurchase";
import {useOverLimitPlanPurchase, type OverLimitPlanPurchase} from "../overLimit/useOverLimitPlanPurchase";
import {
	chosenSizedPlan, closesOnRefusedClaim, plansOnlyResolution, reopenAfterRefusedClaim, sizedPlanOffers,
	type SizedPlanRows,
} from "../overLimit/sizedPlanDialog";
import {offeredPlanId, overLimitPlanMoment, planRowPrice} from "../overLimit/overLimitPayDialog";
import {fileListRefusedLocally, type FileListFacts} from "../overLimit/fileListRules";
import {usePurchaseUseIntent} from "account/purchases/usePurchaseUseIntent";
import {usePaidMoment} from "upsell/usePaidMoment";
import formatBytes from "core/format/byteFormat";

/** Everything a submitted transfer carries whatever its delivery (the contract's ui#49 core). */
interface TransferPayloadFields {
	mode: Mode;
	expireAfter: ExpiresAfter;
	emailsFrom: string;
	/** Optional download password (PRO; API contract v3). */
	password?: string;
	/** Optional download cap (PRO; API contract v3). */
	maxDownloads?: number;
	/**
	 * Per-transfer download-alerts opt-in (api#92): {@code true} only when a sender carrying the
	 * `downloadNotifications` capability armed the "notify" toggle. Omitted otherwise.
	 */
	notifyOnDownload?: boolean;
	/**
	 * Per-transfer expiry-recovery opt-in (api#443, Premium): {@code true} only when an entitled
	 * sender armed the "recovery" toggle — the api then retains the bytes past the link's expiry so
	 * the transfer can be brought back once. Omitted otherwise, which is what keeps the retained
	 * storage unbought for everyone who did not ask for it.
	 */
	recoveryOptIn?: boolean;
	/**
	 * Key-handling flow for the encrypted upload (epic #61): {@code "escrow"} (default) or
	 * {@code "zeroKnowledge"} (Premium, when the ZK toggle is armed and no recipient chips are present).
	 */
	keyMode: KeyMode;
	/**
	 * Per-transfer personalization (api#461), present only when the sender armed the Personalise sheet
	 * and composed something the api will accept.
	 *
	 * Assembled by {@link TransferPanel} from {@link usePersonalizationDraft} rather than here, because
	 * it is a whole feature's state and not one of this form's fields: folding it into `buildPayload`
	 * would make every consumer of the create form carry the personalization composer with it.
	 *
	 * The request and the RUNG it goes out at, because the success card has to know what this send
	 * attached and not merely that it attached something (ui#2411) — see {@link AttachedPersonalization}.
	 */
	personalization?: AttachedPersonalization;
	/**
	 * The company this transfer is sent as (kitecorp/transferify-ui#3186), present only when the sender chose Company
	 * beside the builder's "Your name". Assembled by {@link TransferPanel}, which holds that choice, as it assembles
	 * {@link personalization}; absent sends as a person. Free on every plan.
	 */
	senderCompany?: SenderCompany;
	/**
	 * The settled over-limit purchase this send spends (ui#1454, api#823), present only when the sender
	 * is over their free cap and has paid for the size.
	 *
	 * The init NAMES the allowance; nothing is auto-applied server-side, so a 1 TB purchase can never be
	 * quietly spent on a 6 GB send — and naming one that does not cover the declared size is refused
	 * with `error.transfer.overLimit.notConsumable`.
	 */
	overLimitAllowanceId?: string;
}

/** A plain shareable link: nobody is notified, so there is no selection to carry. */
interface LinkTransferPayload extends TransferPayloadFields {
	delivery: "link";
	recipientSelection: null;
}

/** A recipients send: the same link, plus the unified email + group selection to notify (ui#210). */
interface RecipientsTransferPayload extends TransferPayloadFields {
	delivery: "recipients";
	recipientSelection: RecipientSelection;
}

/**
 * The submit payload contract consumed by {@link CreateFlow}.
 *
 * `delivery` and `recipientSelection` are ONE fact expressed once (ui#890), not two fields a reader has
 * to keep in agreement: a link carrying recipients, and a recipients send with nothing to send to, are
 * both unrepresentable. That is deliberate — once the delivery tabs were dropped and `delivery` became
 * DERIVED from the selection, the runtime guard that used to catch the mismatch could no longer fail,
 * and a check that cannot fail is worse than no check. The compiler carries the invariant instead.
 */
export type TransferPayload = LinkTransferPayload | RecipientsTransferPayload;

interface Params {
	replyTarget?: ReplyTarget | null;
	replyActive?: boolean;
	plan: Plan;
	/** Only count + sizes matter here — any file-shaped view model (or raw `File`) satisfies it. */
	files: ReadonlyArray<{size: number}>;
	/**
	 * The selection's file-list facts, measured on what the upload init will be sent
	 * ({@link ../overLimit/fileListRules.fileListFactsOf}): judged before any over-limit payment step, so a sender is
	 * never sold a send the init refuses (kitecorp/transferify-ui#2930, api kitecorp/transferify-api-service#2144).
	 */
	fileList: FileListFacts;
	isSeeding: boolean;
	authInFlight: boolean;
	/**
	 * The e-mail code step is open (`CreateFlow` owns it). Its opening is the signed-out over-limit moment's press
	 * (kitecorp/transferify-ui#3435), and nothing else reads it. Absent means closed: a composer that owns no code
	 * step opens none.
	 */
	signInStepOpen?: boolean;
	/**
	 * Which way the panel's composer faces (kitecorp/transferify-ui#3140). The code step a Request files press opens
	 * is no press of this form's Send, so the counter above tells the two apart by it. Absent means sending.
	 */
	facing?: ComposerMode;
	/**
	 * A sender reached for a PRO-locked expiry rung or option row, and is routed to the upgrade surface
	 * for the tier that unlocks THAT control.
	 *
	 * <b>The tier travels</b> since kitecorp/transferify-ui#2529, because the surface it opens sells one
	 * plan. It comes from the same `unlockTier` the row's own gold pill reads — `TRANSFER_OPTIONS` for
	 * the option rows and `expiryOptions` for the retention rungs — so the dialog can never offer a tier
	 * that would not actually unlock what the sender just touched (the ui#1203 defect, one level up).
	 */
	onUpgrade: (target: PaidPlanId) => void;
	/**
	 * A wall the sender just met, REPORTED and not routed (kitecorp/transferify-ui#2278): the upsell card
	 * is armed from it. Deliberately separate from {@link onUpgrade}, because the two do not coincide —
	 * choosing the longest retention a paid plan allows is a wall with nothing locked about it, and every
	 * other locked control still routes to an upgrade surface exactly as before.
	 */
	onWall?: (wall: UpsellTrigger) => void;
	/**
	 * The api refused the last send because the account's live transfers already fill its storage
	 * (ui#1664, CreateFlow's `activeStorageRefused`). It arms the quote and the purchase read for the storage wall
	 * (`passWallFor`, kitecorp/transferify-ui#3046), so a pass held from before the one-off stopped is still read as
	 * paid, and it arms the strip's funnel; the next submit clears it.
	 */
	storageRefused?: boolean;
	/**
	 * The registration was refused for a plan, and the wall it was refused on: `"instant"` for
	 * `error.transfer.instant.planRequired` (kitecorp/transferify-api-service#3005, Q5), `"hosted"` for
	 * `error.transfer.free.maxUpload` (kitecorp/transferify-ui#3800); CreateFlow's `planRequiredRefused`. Once the wall
	 * holds an offered plan the form opens the plan dialog by itself and calls {@link onPlanRefusalHandled}; the next
	 * submit clears it. Null (the default) when nothing was refused.
	 */
	planRequiredRefusal?: OverLimitPricing | null;
	/** The refusal was acted on (the dialog opened), so its owner drops it and it is not acted on twice. */
	onPlanRefusalHandled?: () => void;
	/**
	 * Why the transfer cannot go out as the company it is sent as yet (kitecorp/transferify-ui#3186): the panel
	 * holds the sender choice, so it answers this from `senderBlocker`. Absent or null sends as before.
	 */
	senderBlock?: SenderBlocker | null;
	/**
	 * The composer holds files the account already stores (kitecorp/transferify-ui#3405): the send is a new link
	 * to them, always hosted and never Zero-Knowledge (`storedFilesMode`, `resolveZeroKnowledge`), and its submit
	 * never says "upload", because nothing is uploaded.
	 */
	storedFiles?: boolean;
}

/**
 * One rung of the expiry ladder, as the options sheet's picker renders it (ui#1201).
 *
 * There is deliberately no `selected` flag: the view is a listbox driven by {@link CreateFormVM.expireAfter},
 * so it derives the current rung from that one value. A second copy of the same fact is a copy that can
 * disagree with it.
 */
export interface ExpiryChoiceVM {
	value: ExpiresAfter;
	label: string;
	/**
	 * The upsell badge for a rung this plan may NOT pick, or `null` for one it may — so "is this rung
	 * locked" and "which tier does its badge name" are one value rather than a boolean beside a
	 * separately-chosen string. The separately-chosen string was fixed at BASIC and mislabelled the
	 * PREMIUM-only indefinite rung (ui#1203).
	 */
	locked: LockedBadge | null;
	select: () => void;
}

/** The submit button's resolved copy + disabled state for the view (label localized in the hook). */
export interface SubmitVM {
	label: string;
	disabled: boolean;
}

/**
 * The download-password field's helper line (ui#384): the api's bounds while the field is untouched or
 * valid, the specific refusal — as an error — once something outside them is typed. Localized here,
 * like the submit label, so the view renders one string and decides nothing.
 */
export interface PasswordHintVM {
	text: string;
	error: boolean;
}

/** Actionbar option-switch row view-model (design `optsDisp`). */
export interface OptionVM {
	key: OptionKey;
	label: string;
	desc: string;
	icon: string;
	on: boolean;
	disabled: boolean;
	/** Gold PRO tag shown to users below the option's unlock tier. */
	proPill: boolean;
	/**
	 * The tier name the locked pill advertises — "BASIC" through download alerts (api#92), and
	 * "PREMIUM" for expiry recovery (api#443).
	 */
	pillLabel: string;
	/**
	 * Plain-language explanation of what arming this row actually does, revealed from an info glyph on
	 * hover and on keyboard focus (api#443). Undefined for the rows whose one-line `desc` already says
	 * everything — a hint that repeats the sub-line is noise, not help.
	 */
	hint?: string;
	toggle: () => void;
}

export interface CreateFormVM extends RecipientsFieldVM {
	plan: Plan;
	mode: Mode;
	setMode: (mode: Mode) => void;
	senderEmail: string;
	setSenderEmail: (value: string) => void;
	/** Authenticated account identity is canonical and cannot be edited into a send-on-behalf value. */
	senderEmailLocked: boolean;

	/** Password option. */
	passwordOn: boolean;
	password: string;
	setPassword: (value: string) => void;
	passwordHint: PasswordHintVM;
	/** Download-limit option. */
	dlimitOn: boolean;
	dlimit: string;
	setDlimit: (value: string) => void;
	options: OptionVM[];

	expireAfter: ExpiresAfter;
	expiryLabel: string;
	expiryChoices: ExpiryChoiceVM[];

	/**
	 * The selection exceeds the viewer's own plan cap, on EITHER delivery path (ui#1719).
	 *
	 * The size FACT only. What it means is {@link overLimit}'s `resolution`, which is where an instant
	 * send by a subscriber, or one the api will not sell, still comes out clear.
	 */
	overCap: boolean;
	/** The actual byte route: email is hosted even when the Instant tab is selected. */
	hostedUpload: boolean;
	/**
	 * Localized current-tier/next-tier UPGRADE guidance.
	 *
	 * Since ui#1454 it is withheld wherever a PURCHASE is on offer or in flight — that is the surface
	 * the owner's single-CTA constraint binds. It survives only where there is no checkout to compete
	 * with: a PAID plan over its own cap ({@link overLimit}'s `upgradeOnly`).
	 */
	overCapMessage: string | null;
	/** The over-cap priced path (ui#1454): what it costs, and the one action that buys it. */
	overLimit: OverLimitVM;
	/** The storage-full strip's pass and funnel (kitecorp/transferify-ui#3046). */
	activeStorage: ActiveStorageVM;
	/** Localized current-plan hosted-file size limit — shown in the options sheet footer (ui#214). */
	hostedLimitLabel: string;
	submit: SubmitVM;
	buildPayload: () => TransferPayload;
}

/**
 * The over-cap purchase as the panel renders it (ui#1454).
 *
 * Everything here is either the SERVER's answer or a formatting of it. There is no price arithmetic on
 * this side: the ladder lives in the api (#823) and a client copy of it would drift the moment the
 * owner repriced.
 */
export interface OverLimitVM {
	/**
	 * What the over-cap selection means — the rules module's single value, as the SIZE wall's surfaces read
	 * it: `clear` whenever the purchase is for the storage wall instead ({@link sizeWallResolution}).
	 */
	resolution: OverLimitResolution;
	/** Which rule prices this send; the pay dialog's lede and rows read it (kitecorp/transferify-ui#3229). */
	pricing: OverLimitPricingOf;
	/** The selection's size as the sender reads it, for the dialog's lede. */
	sizeLabel: string;
	/**
	 * The plan row in view (kitecorp/transferify-ui#3229): the offer of the row {@link sized} has chosen, so the plan
	 * funnel and the plan checkout follow the row on screen. Null whenever `sized` is null.
	 */
	planOffer: OverLimitPlanOffer | null;
	/**
	 * The sized plan dialog's rows (kitecorp/transferify-ui#3800): every paid plan that holds the drop and the one in
	 * view, with the sender's pick, on the size wall of either delivery path, or null on any other wall or where no plan
	 * holds the drop. When set, {@link planOffer} is the chosen row's offer, so the plan funnel and the plan checkout
	 * follow the row on screen.
	 */
	sized: SizedPlanRows | null;
	/** A placed plan order is awaiting its switch-on: the strip says so. */
	confirming: boolean;
	/**
	 * The plan wall's press, on either path: opens the payment dialog on the plans, the smallest chosen, with no
	 * session minted (kitecorp/transferify-api-service#3005, Q5; kitecorp/transferify-ui#3800).
	 */
	startPlanChoice: () => void;
	/** Whether the payment dialog is on screen, choosing a row or paying for the chosen one. */
	paymentOpen: boolean;
	/** The sender chose the plan row and pressed Continue: the plan row's own claim step. */
	continueWithPlan: () => void;
	/** The plan row's purchase: its consent, Session, refusals and the wait for the plan to switch on. */
	plan: OverLimitPlanPurchase;
	/** Our order button placed the plan's order: the dialog closes and the composer waits for the plan. */
	planPlaced: () => void;
	/** Abandon the attempt. The selection survives; a settled allowance survives too. */
	dismissPayment: () => void;
	/** Re-ask a quote that gave up — what the single CTA does on the `quoteFailed` rung. */
	retryQuote: () => void;
}

type OverLimitPricingOf = ReturnType<typeof overLimitPricingOf>;

/**
 * The storage-full strip as the panel renders it (kitecorp/transferify-ui#3046): whether a held pass pays for this
 * send, beside the strip's two exits, and the strip's own funnel.
 */
export interface ActiveStorageVM {
	/** A held pass pays for this send — {@link storagePassPaid}'s answer. */
	passPaid: boolean;
	/** The strip's Upgrade was pressed: counted on the strip's own moment, wherever the press then goes. */
	reportUpgradeClicked: () => void;
}

/**
 * TransferPanel-level state + derivations for the create card (ui#49). Owns every editable field,
 * the paid-option switches, the actionbar popover, and the derived submit state — so the panel view
 * stays dumb (props/tokens only, epic #26). Pure decisions delegate to {@link createFormLogic}.
 */
export function useCreateForm({
	plan, files, fileList, isSeeding, authInFlight, signInStepOpen = false, facing = "send", onUpgrade, onWall,
	replyTarget = null, replyActive = false, storageRefused = false, planRequiredRefusal = null,
	onPlanRefusalHandled, senderBlock = null, storedFiles = false,
}: Params): CreateFormVM {
	const {sender, pricing, reminders} = useMessages();
	const {locale} = useLocale();
	// The refusal returns the sender to a NEW composer, because the panel is unmounted while a send runs. An Instant
	// send refused for a plan starts it on Instant, since hosted would put the hosted wall where the plan dialog is
	// due; a hosted refusal is already on the default path, so the path is kept across that one return.
	const [pickedMode, setMode] = useState<Mode>(planRequiredRefusal === "instant" ? "instant" : "upload");
	const mode = storedFilesMode(replyMode(pickedMode, replyActive), storedFiles);
	// Title + Message left this form entirely in ui#1814 (owner, 2026-09-01). They lived in the options
	// sheet from ui#216 until then, seeded with friendly editable copy; the personalization builder now
	// owns the sender's note, and `PersonalizationDraft.message` — free on every plan since api#1099 —
	// is the one place it is held. The recipient page already preferred that field over this one, so a
	// second transfer-level copy could only ever have been the invisible one.
	const [typedSenderEmail, setTypedSenderEmail] = useState("");
	const recipientsField = useRecipientsField(plan);
	const {selection, seed: seedRecipients} = recipientsField;
	const seededReply = useRef<string | null>(null);
	useEffect(() => {
		const seed = replyRecipientSeed(replyTarget, replyActive, seededReply.current);
		if (seed === null) return;
		seededReply.current = replyTarget!.transferId;
		seedRecipients(seed);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [replyTarget?.transferId, replyActive]);
	// Every switch starts OFF, recovery included (api#443): the owner's rule is that a transfer is only
	// recoverable when the sender explicitly checked the box.
	const [opts, setOpts] = useState<Record<OptionKey, boolean>>(
		{password: false, dlimit: false, zeroKnowledge: false, notify: false, recovery: false});
	const [password, setPassword] = useState("");
	const [dlimit, setDlimit] = useState("");
	// The picked retention is coerced through the CURRENT plan's catalogue window on every render
	// (ui#304): the plan resolves after mount (login/account hydration), and the api rejects an
	// out-of-window value (api#204), so a stale pick must fall back to the plan's default.
	const [pickedExpiry, setExpireAfter] = useState<ExpiresAfter>(defaultExpiry(plan));
	const expireAfter = coerceExpiry(plan, pickedExpiry);

	// Prefill the sender with the logged-in account email (ui#145): the sender is mandatory on every
	// transfer (ui#71), so a logged-in user re-typing their own address on each send is needless
	// friction (and a typo can trip the verification overlay, ui#131). `useIsLoggedIn` is the
	// hydration-safe read (false on the server + first client render), so the account email is only
	// known after mount — hence we seed it in an EFFECT, not initial state, keeping the SSG/first
	// client render byte-identical to the logged-out HTML (CreateFlow renders in both auth states).
	const [user] = useUser();
	const accountEmail = useIsLoggedIn() && user.email ? user.email : "";
	const senderEmail = canonicalSenderEmail(typedSenderEmail, accountEmail);
	const senderEmailLocked = accountEmail !== "";
	const setSenderEmail = (value: string) => {
		setTypedSenderEmail(canonicalSenderEmail(value, accountEmail));
	};
	useEffect(() => {
		if (!accountEmail) return;
		// Replace the underlying draft too, so logging out cannot resurrect a sender that belonged to a
		// pre-authentication form. The render-time canonicalization above closes the effect timing gap.
		setTypedSenderEmail(accountEmail);
	}, [accountEmail]);

	const fileCount = files.length;
	// The unified field IS the delivery decision (ui#890): no chips is a plain link, any chip makes it
	// a recipients send. Derived on every render rather than stored, so the two can never disagree.
	const zeroKnowledge = resolveZeroKnowledge(plan, opts.zeroKnowledge, selection, storedFiles);
	const optionOverrides: Partial<Record<OptionKey, Pick<OptionVM, "on" | "disabled" | "desc">>> = {
		zeroKnowledge: {
			on: zeroKnowledge.on,
			disabled: zeroKnowledge.disabled,
			desc: storedFiles
				? sender.options.zeroKnowledge.storedUnavailable
				: zeroKnowledge.disabled
					? sender.options.zeroKnowledge.recipientsUnavailable
					: sender.options.zeroKnowledge.desc,
		},
	};
	const delivery = deliveryOf(selection);
	const hostedUpload = usesHostedUpload(mode, delivery);
	// WHICH RULE prices a way past the cap on this delivery path (ui#1719): hosted scales per 10 GB
	// because we store the bytes, instant is flat because we do not.
	const overLimitPricing = overLimitPricingOf(mode, delivery);
	// Summed ONCE, and read from here by both the quote and the cap check: a folder drop is thousands of
	// entries walked on every keystroke-sized change in this composer.
	//
	// The DECLARED total, not the hosted one, since ui#1719: an instant transfer uploads no bytes but
	// still weighs its files, and that weight is what the api quotes and enforces against. Reading the
	// hosted figure here was what made the instant path free at any size — correct while the api had no
	// instant paywall, and a hole the moment it grew one.
	const selectedBytes = declaredSelectionBytes(files);
	const overCap = isOverUploadCap(plan, selectedBytes);
	const canUseOptions = canUseTransferOptions(plan);
	const nextPlan = nextUpgradePlan(plan);
	const currentLimit = hostedUploadLimitLabel(plan, pricing.compare.unlimited, locale);

	// Whether there is an ACCOUNT to own a purchase. An anonymous Firebase session is not one and must
	// not count: the api's over-limit routes are account-scoped, so they would answer 401.
	const overLimitSignedIn = accountEmail !== "";
	// The over-cap quote and purchase read (ui#1454). ACTIVE ONLY FOR FREE — the plan wall is FREE's; a paid tier
	// over its cap gets the upgrade nudge (`upgradeOnly`), so no quote is even fetched for a subscriber, which is the
	// first of the three places a subscribed sender is kept away from this checkout (see `resolveOverLimit`).
	//
	// SIGNED IN is part of the audience for the same kind of reason: `planOf` answers "free" for a
	// signed-out visitor and this panel renders for them too, so quoting here would 401 against the
	// api's authenticated over-limit routes. Withholding the FETCH was never the whole answer though,
	// and assuming it was is what shipped ui#1715: the resolution below did not know this audience
	// existed, so a signed-out sender got `quoting` — a permanent spinner over a request that was
	// never going to be made — instead of the sign-in that unlocks the purchase. The rules module is
	// told who the sender is now, and answers `signInRequired`, which renders nothing and leaves the
	// usual Send open as the way to that sign-in (kitecorp/transferify-ui#3435).
	//
	// Arming still follows sign-in automatically: `accountEmail` fills in the moment the composer's
	// own gate completes, which flips both this and the resolution in the same render.
	//
	// The same read serves the storage-full strip (kitecorp/transferify-ui#3046): a FREE account whose live storage
	// cannot hold the send is read for a held purchase at any size, so WHICH wall it is read for is the rule's
	// answer, and every size-wall surface below reads its gated resolution.
	const wall = passWallFor(overCap, storageRefused, overLimitPricing);
	// THE FILE COUNT IS JUDGED HERE, before anything is asked (kitecorp/transferify-ui#2930): against the limit the api
	// publishes on `/plan`, never a copy. A selection the upload init would refuse is quoted nothing, so no price, no
	// checkout and no open Send leading into a sign-in to pay ever appears for it, and the api's limit-wall email is
	// never armed for it. The
	// name and type rules have no published figure; the quote judges those and its refusal arrives through the hook.
	const localFileListRefusal = fileListRefusedLocally(fileList, maxFilesPerTransfer());
	const purchase = useOverLimitPurchase({
		active: wall !== null && plan === "free" && overLimitSignedIn && localFileListRefusal === null,
		bytes: selectedBytes,
		pricing: overLimitPricing,
		files: fileList,
	});
	// The purchase the reader pressed "Use it" on (kitecorp/transferify-ui#3004), if any. It stands in
	// for the api's best-fit candidate on the init where the listing proves the api would take it: its quoted
	// size on the type it was bought for, or a hosted purchase on an instant send
	// (kitecorp/transferify-api-service#2787); the quote and the allowance read in `useOverLimitPurchase` above stay on
	// the api's candidate.
	const chosenPurchase = usePurchaseUseIntent().purchase;
	// The sized plan dialog (kitecorp/transferify-ui#3800): on the SIZE wall of either path, the plans that hold the drop,
	// and the plan wall in place of the one-off wall they replace, because no one-off is on sale. The storage strip
	// offers no pass either. Every reader below reads the mapped rung.
	// Memoised so `sized.plans` keeps one identity across renders while its inputs stand, for whatever keys on it.
	const sizedPlans = useMemo(
		() => sizedPlanOffers(wall, overLimitPricing, purchase.offer, selectedBytes),
		[wall, overLimitPricing, purchase.offer, selectedBytes]);
	const [sizedChoice, setSizedChoice] = useState<PaidPlanId | null>(null);
	const sizedRow = chosenSizedPlan(sizedChoice, sizedPlans);
	const overLimitResolution = plansOnlyResolution(resolveOverLimit({
		pricing: overLimitPricing, overCap: wall !== null, isFreePlan: plan === "free", signedIn: overLimitSignedIn,
		selectedBytes,
		offer: purchase.offer,
		allowance: preferredOverLimitAllowance(purchase.allowance, chosenPurchase, selectedBytes, overLimitPricing),
		allowanceAnswered: purchase.allowanceAnswered,
		quoteFailed: purchase.quoteFailed,
		fileListRefusal: localFileListRefusal ?? purchase.fileListRefusal,
		// The catalogue's own rule for every tier (a paid tier asks for no quote), or the quote's refusal for FREE.
		aboveLargestPlan: isHostedSelectionAboveEveryPlan(mode, delivery, files) || purchase.aboveLargestPlan,
	}), wall, sizedPlans);
	const sizeResolution = sizeWallResolution(wall, overLimitResolution);
	const passPaid = storagePassPaid(wall, overLimitResolution);
	// Named on the init from the SHARED answer: a settled pass is spent the same way on either wall.
	const allowanceId = overLimitAllowanceId(overLimitResolution);
	// The paid-moment funnel's seam for the over-cap wall (kitecorp/transferify-ui#1965). WHICH wall and
	// whether it is up are the rule module's answers; this hook only names them on the emitter. THE CLOSE IS
	// REPORTED: closing the size wall's sheet is OFFER_DISMISSED on the wall (owner decision 10 of
	// kitecorp/transferify-ui#3022), the denominator its conversion is read against; the strip itself has no
	// control to walk away with.
	// THE SIGNED-OUT MOMENT'S FUNNEL (kitecorp/transferify-ui#3196): the signed-out rung is a surface of its own,
	// reported anonymously, because the priced wall's funnel counts accounts and this reader has none yet. Nothing
	// renders for it since kitecorp/transferify-ui#3435, and reaching it still counts as shown, so the step-1 series
	// keeps its meaning of "senders who reached it". Its press is the e-mail code step opening from it, the sign-in
	// the usual Send starts. Reaching it is REMEMBERED for the tab's session, so every signed-in over-limit step
	// after the sign-in can say it followed this moment; the rule module owns the mark, and the effect only runs it.
	//
	// NOT BEFORE THE SESSION IS KNOWN. `useIsLoggedIn` answers false on the first client render on purpose, to
	// match the pre-rendered signed-out HTML (ui#74), so a returning signed-in sender whose files are already in
	// hand on that render meets the signed-out rung for one frame. Counting that frame would report a moment
	// nobody reached and mark the session as arrived through it; the count is held until the session has mounted
	// and the rung is still the signed-out one.
	//
	// NOR FOR A SESSION THAT HAS AN ACCOUNT BUT NO EMAIL. `overLimitSignedIn` reads the account EMAIL, because the
	// purchase needs one, so a signed-in account whose provider gave none meets the signed-out rung too. That sender
	// has a session, so the beacon would carry a signed-out report to the authenticated route, where the api files
	// it as a drifted client. The signed-out moment is the rung AND no session at all.
	const sessionKnown = useHasMounted();
	const signedOutReached = sessionKnown && !user.isLoggedIn && overLimitSignedOutReached(sizeResolution);
	const signedOutFunnel = usePaidMoment(overLimitSignedOutMoment(overLimitPricing), {
		visible: signedOutReached,
		price: null,
	});
	useEffect(() => {
		if (signedOutReached) {
			rememberSignedOutWall(browserSessionStorage());
		}
	}, [signedOutReached]);
	// The press, read against the step's previous state so one opening is one press however often this renders.
	const signInStepWasOpen = useRef(signInStepOpen);
	useEffect(() => {
		const opened = overLimitSignedOutSignInOpened({
			reached: signedOutReached, facing, wasOpen: signInStepWasOpen.current, open: signInStepOpen,
		});
		signInStepWasOpen.current = signInStepOpen;
		if (opened) {
			signedOutFunnel.reportClicked();
		}
	}, [signInStepOpen, signedOutReached, facing]);
	// Read per render rather than held in state: the mark lives in the tab's session store so it survives the reload a
	// sign-in can cause, and the funnel adapters read their detail at emit time, so the signed-in wall's own showing
	// carries the answer as of the render that put it on screen. Passed on the two funnels below, each one of two
	// surfaces by pricing rule (the priced wall and the pay dialog's plan row), which are the four
	// surfaces the api admits it on, and nowhere else.
	const arrivedSignedOut = arrivedSignedOutIn(browserSessionStorage());
	const overLimitFunnel = usePaidMoment(overLimitMoment(overLimitPricing), {
		visible: overLimitWallShown(sizeResolution),
		// The wall states no price: its press opens the plans, whose row reports its own figure below.
		price: null,
		detail: {arrivedSignedOut},
	});
	// THE PLAN ROW (kitecorp/transferify-ui#3229): the paid plan the dialog has in view, or null. Its
	// funnel is the plan moment of this wall, shown while the dialog is up with the row on it, priced at the first
	// month and naming the plan offered on every step, so the question splits kept plans per 100 dialogs by plan.
	// The row in view is the plan offered, so the funnel and the plan checkout follow the sender's pick; every row the
	// dialog can show comes through `sizedPlans`, the quote's single row included.
	const planOffered = sizedRow === null ? null : sizedRow.offer;
	const offeredPlan = offeredPlanId(planOffered);
	const planFunnel = usePaidMoment(overLimitPlanMoment(overLimitPricing), {
		visible: purchase.paymentOpen && offeredPlan !== null,
		price: planRowPrice(planOffered),
		detail: {...(offeredPlan === null ? {} : {targetPlan: planCodeForId(offeredPlan)}), arrivedSignedOut},
	});
	const planPurchase = useOverLimitPlanPurchase({
		open: purchase.paymentOpen, planOffer: planOffered, bytes: selectedBytes, pricing: overLimitPricing,
		moment: planFunnel,
	});
	// Whether the open dialog offered the row, read at close time: the dialog's dismissal is the row's too.
	const planOfferedRef = useRef(offeredPlan !== null);
	planOfferedRef.current = offeredPlan !== null;
	// THE STORAGE STRIP'S FUNNEL (kitecorp/transferify-ui#2664): one instance, so the strip's showing is never
	// counted twice. The strip states no price, since no pass is sold on it. Its Upgrade press reports on it through
	// the view model.
	const storageFunnel = usePaidMoment("ACTIVE_STORAGE_FULL", {
		visible: storageRefused,
		price: null,
	});
	// The wall at close time, read through a ref: the close handler is not rebuilt per render, and the dismissal
	// belongs to the funnel of the wall the sender was looking at.
	const wallRef = useRef(wall);
	wallRef.current = wall;

	const continueWithPlan = useCallback(() => planFunnel.reportClicked(), [planFunnel]);

	// A REGISTRATION REFUSED FOR A PLAN, ON EITHER WALL (Instant: kitecorp/transferify-api-service#3005, Q5; hosted:
	// kitecorp/transferify-ui#3800): the sender pressed Send before the strip had stopped it, so the answer is the sized
	// plan dialog, the smallest fitting plan chosen, not a sentence. Run once the wall holds an offered plan, which may be
	// later than the refusal (the quote or the purchase read can still be on the way), and handed back to the owner so
	// the same refusal never opens it twice.
	const planDialogDue = planDialogOpensAfterRefusal(planRequiredRefusal !== null, sizeResolution, purchase.paymentOpen);
	useEffect(() => {
		if (planDialogDue) {
			purchase.startPlanChoice();
			onPlanRefusalHandled?.();
		}
	}, [planDialogDue, purchase.startPlanChoice, onPlanRefusalHandled]);

	// The plan's order was placed on our button: the dialog closes, and the purchase hook waits for the plan.
	// NOT a dismissal of either surface, so neither funnel reads a walk-away.
	const planPlaced = useCallback(() => {
		planPurchase.placed();
		purchase.dismissPayment();
		setSizedChoice(null);
	}, [planPurchase, purchase.dismissPayment]);

	// Closing the sheet is the size wall's dismissal while that wall is up; the sheet opens on no other wall.
	// The dialog's close is the plan row's dismissal as well, whenever the row was on it (ui#3229).
	const dismissOverLimitPayment = useCallback(() => {
		if (sheetCloseDismissesWall(wallRef.current)) {
			overLimitFunnel.reportDismissed();
		}
		if (planOfferedRef.current) {
			planFunnel.reportDismissed();
		}
		purchase.dismissPayment();
		// Every opening preselects the smallest plan again (R2), so a close forgets the sender's pick.
		setSizedChoice(null);
	}, [overLimitFunnel, planFunnel, purchase.dismissPayment]);

	// A REFUSED PLAN ORDER, on both walls (the owner, 2026-10-27: "let's keep things simple, not have different flows").
	// The api refuses a claim only when it is not one of the rows it just offered (a stale quote, a forged request), so
	// the order form has no error state to show: the form closes, the quote is asked again, and the same sized dialog
	// reopens on the fresh rows, the smallest fitting plan chosen. When the fresh quote offers no plan, the reopening is
	// dropped and the strip states what the api offers now. `purchase.dismissPayment` rather than the dialog's own
	// close: the sender dismissed nothing, so no funnel reports OFFER_DISMISSED.
	//
	// `refusal` marks the refusal that is waiting for its quote and keeps the count of answers the purchase had settled
	// when it was refused. The quote is renewed once the count has moved past it: every settled answer moves it (an
	// offer, no offer, a failure), and an answer that arrived before the refusal is not one. The offer itself is no
	// signal: it is cleared and set again inside one batch when the answer comes at once.
	const [refusal, setRefusal] = useState<{answersAtRefusal: number} | null>(null);
	const claimRefused = closesOnRefusedClaim(purchase.paymentOpen, planPurchase.offerUnavailable);
	const quoteAnswers = purchase.quoteAnswers;
	useEffect(() => {
		if (claimRefused) {
			purchase.dismissPayment();
			purchase.retryQuote();
			setSizedChoice(null);
			setRefusal({answersAtRefusal: quoteAnswers});
		}
	}, [claimRefused, quoteAnswers, purchase.dismissPayment, purchase.retryQuote]);
	// A selection taken away ends the wait: its quote will never come, and the next selection is a new question.
	const wallGone = wall === null;
	useEffect(() => {
		if (wallGone) {
			setRefusal(null);
		}
	}, [wallGone]);
	// The size wall's rung: the storage strip offers no plan row, so a refusal that outlived the size wall is dropped there
	// instead of reopening an empty dialog.
	const afterRefusal = reopenAfterRefusedClaim(
		refusal !== null, refusal !== null && quoteAnswers > refusal.answersAtRefusal, sizeResolution, purchase.paymentOpen);
	useEffect(() => {
		if (afterRefusal === "reopen") {
			purchase.startPlanChoice();
		}
		if (afterRefusal !== null) {
			setRefusal(null);
		}
	}, [afterRefusal, purchase.startPlanChoice]);

	// The upgrade nudge is now the PAID tiers' answer alone. A FREE sender over the cap gets the plan wall
	// instead, with no subscription pitch competing beside it (owner constraint, ui#1454) — which is why
	// this reads the resolution rather than `overCap`.
	const overCapMessage = overLimitShowsUpgradeNudge(sizeResolution)
		? nextPlan === null
			? sender.hostedLimitExceeded(pricing.planNames[plan], currentLimit)
			: sender.hostedLimitUpgrade(
				pricing.planNames[plan],
				currentLimit,
				pricing.planNames[nextPlan],
				hostedUploadLimitLabel(nextPlan, pricing.compare.unlimited, locale),
			)
		: null;

	/** Free users cannot arm a PRO option — nudge them to pricing and leave the switch off. Each
	 * option carries its own capability gate (password/dlimit vs Zero-Knowledge — ui#65). */
	const toggleOption = (key: OptionKey) => {
		if (optionOverrides[key]?.disabled) return;
		const def = TRANSFER_OPTIONS.find(option => option.key === key);
		if (def && !def.canUse(plan)) {
			// EVERY locked option is an upsell-card wall (owner 2026-09-17, ui#2514): the card names the
			// control the sender touched and sells the tier that unlocks it. Which tier that is is not
			// decided here — `upsellCard` reads the same `unlockTier` this row's pill does.
			onWall?.({control: "option", option: key});
			// ...and the SAME tier routes the upgrade surface (ui#2529): the card and the dialog behind
			// the press must sell one plan, not two answers to "what unlocks this row".
			onUpgrade(def.unlockTier);
			return;
		}
		setOpts(prev => ({...prev, [key]: !prev[key]}));
	};

	const recipientSelectionValid = recipientsField.selectionValid;

	// Every option row is always visible (ui#306 — E2EE is fully live, the rollout flag is retired);
	// entitlement gating happens per option via canUse/proPill.
	//
	// ONE tier→copy table for BOTH gated surfaces in this sheet — the option rows and the expiry
	// rungs. The rungs used to reach past it for a fixed `sender.proPill`, which is how a rung that
	// unlocks at PREMIUM came to advertise BASIC (ui#1203).
	const pillLabels = {basic: sender.proPill, plus: sender.plusPill, premium: sender.premiumPill};
	const lockedTierCopy: LockedTierCopy = {
		pills: pillLabels,
		lockedAria: sender.optionsSheet.expiryLockedAria,
	};
	const options: OptionVM[] = TRANSFER_OPTIONS.map(def => ({
		key: def.key,
		label: sender.options[def.key].label,
		desc: sender.options[def.key].desc,
		icon: def.icon,
		on: opts[def.key],
		disabled: false,
		...optionOverrides[def.key],
		proPill: !def.canUse(plan),
		pillLabel: pillLabelFor(def.unlockTier, pillLabels),
		hint: optionHintFor(def, sender.optionHints),
		toggle: () => toggleOption(def.key),
	}));

	// The retention label rule lives in `home/expiryLabel` (kitecorp/transferify#90). It used to live here,
	// which meant the indefinite rung was handled on THIS control and nowhere else — the success card and
	// the extend sheet formatted their own and would have rendered "NaN days" for anything without a day count.
	const labelOf = (value: ExpiresAfter) => expiryLabelOf(sender, value);

	const choices = expiryOptions(plan);
	const expiryChoices: ExpiryChoiceVM[] = choices.map(rung => ({
		value: rung.value,
		label: labelOf(rung.value),
		// `unlockTier` decides BOTH halves — whether the rung is locked at all, and what its badge
		// names — so the badge can never advertise a tier the gate does not agree with (ui#1203).
		locked: rung.unlockTier === null ? null : lockedBadgeFor(rung.unlockTier, lockedTierCopy),
		select: () => {
			if (rung.unlockTier !== null) {
				// The RUNG travels, not just "an expiry wall": the card quotes the day count the sender
				// touched and sells the tier that admits it (ui#2514), so a free sender who reached for
				// thirty days is sold Plus rather than Basic's seven.
				onWall?.({control: "expiry", rung: rung.value});
				// The tier that admits THIS rung, from the rung's own gate rather than the next step up:
				// a free sender who reached for thirty days is sold Plus, not Basic's seven (ui#2529).
				onUpgrade(rung.unlockTier);
				return;
			}
			// An ENTITLED rung can still be a wall: the longest one this plan carries is the sender saying
			// they want more than it holds. Nothing is locked, so nothing routes — the card is armed and
			// the retention applies as asked.
			if (isPlanMaxExpiry(plan, rung.value)) onWall?.({control: "maxExpiry"});
			setExpireAfter(rung.value);
		},
	}));
	const expiryLabel = labelOf(expireAfter);

	const senderValid = isValidSender(senderEmail);
	// Only an ARMED password is validated (ui#384): with the switch off no password travels at all, and
	// `buildPayload` below applies the same gate.
	const passwordIssue = canUseOptions && opts.password ? transferPasswordIssue(password) : null;
	const passwordFeedback = transferPasswordFeedback(passwordIssue);
	const passwordHint: PasswordHintVM = {
		text: sender.optionsSheet.passwordFeedback[passwordFeedback.key]({
			min: MIN_TRANSFER_PASSWORD_LENGTH, max: MAX_TRANSFER_PASSWORD_BYTES,
		}),
		error: passwordFeedback.error,
	};
	const submitState = resolveSubmit({
		fileCount,
		mode,
		delivery,
		senderValid,
		// The SIZE verdict, not the size fact: a paid over-cap send submits. `overLimitBlocksSend`
		// passes `clear`, `paid` and the signed-out rung, whose Send leads to the sign-in and creates
		// nothing (kitecorp/transferify-ui#3435), and stops everything else — including `quoting`, so a
		// send cannot race the price it is about to be charged. The size wall's resolution only: the storage wall
		// never holds Send shut (see `sizeWallResolution`).
		sizeBlocked: overLimitBlocksSend(sizeResolution),
		recipientsValid: recipientSelectionValid,
		passwordValid: passwordIssue === null,
		isSeeding,
		authInFlight,
		senderBlock,
	});
	const submitLabels: Record<SubmitStatus, string> = {
		noFiles: sender.submit.noFiles,
		noSender: sender.submit.addEmail,
		badPassword: sender.submit.badPassword,
		// The words Request files uses for the same two reasons (kitecorp/transferify-ui#3186): one company, one copy.
		addCompanyName: reminders.create.submit.addCompanyName,
		checkCompanyId: reminders.create.submit.checkCompanyId,
		instant: sender.submit.instant,
		upload: storedFiles ? sender.submit.storedLink : sender.submit.upload,
	};
	const submit: SubmitVM = {label: submitLabels[submitState.status], disabled: submitState.disabled};

	const buildPayload = (): TransferPayload => {
		const fields: TransferPayloadFields = {
			mode,
			// Instant links live while the tab is open — no TTL, whatever the (upload-mode) picker holds.
			expireAfter: hostedUpload ? expireAfter : INSTANT_EXPIRY,
			emailsFrom: senderEmail,
			password: canUseOptions && opts.password && password ? password : undefined,
			maxDownloads: canUseOptions && opts.dlimit ? sanitizeMaxDownloads(dlimit) : undefined,
			// Download alerts (api#92) carry their own catalogue capability gate — deliberately NOT
			// canUseOptions, even though both capabilities currently unlock at Basic.
			notifyOnDownload: canUseDownloadNotifications(plan) && opts.notify ? true : undefined,
			// Expiry recovery (api#443) carries its own Premium-only gate — re-applied here as well as in
			// toggleOption so a state left armed by an earlier plan can never buy retained storage.
			recoveryOptIn: canRecoverExpiredTransfers(plan) && opts.recovery ? true : undefined,
			keyMode: zeroKnowledge.keyMode,
			// The paid over-cap allowance (ui#1454), NAMED rather than auto-applied. `null` for every
			// resolution but `paid`, and the omission is what keeps an ordinary send from spending a
			// purchase it did not need.
			...(allowanceId === null ? {} : {overLimitAllowanceId: allowanceId}),
		};
		// The one place the delivery and the selection are paired, from the SAME derivation (ui#890) —
		// which is what lets the payload type forbid the two ever disagreeing downstream.
		return isRecipientDelivery(delivery)
			? {...fields, delivery: "recipients", recipientSelection: selection}
			: {...fields, delivery: "link", recipientSelection: null};
	};

	return {
		plan, mode, setMode,
		senderEmail, setSenderEmail, senderEmailLocked,
		recipients: recipientsField.recipients, selectedGroups: recipientsField.selectedGroups,
		selectGroups: recipientsField.selectGroups, removeGroup: recipientsField.removeGroup,
		recipientError: recipientsField.recipientError, recipientCanUpgrade: recipientsField.recipientCanUpgrade,
		recipInput: recipientsField.recipInput, setRecipInput: recipientsField.setRecipInput,
		commitRecipient: recipientsField.commitRecipient, addRecipients: recipientsField.addRecipients,
		removeRecipient: recipientsField.removeRecipient, recip: recipientsField.recip,
		passwordOn: opts.password, password, setPassword, passwordHint,
		dlimitOn: opts.dlimit, dlimit, setDlimit, options,
		expireAfter, expiryLabel, expiryChoices,
		overCap, hostedUpload, overCapMessage, hostedLimitLabel: currentLimit,
		overLimit: {
			resolution: sizeResolution,
			pricing: overLimitPricing,
			// One decimal at most and none for a whole figure ("6 GB", "6.5 GB"): the dialog's lede reads it
			// mid-sentence, where "6.0 GB" reads as a measurement rather than a size.
			sizeLabel: formatBytes(selectedBytes, locale, 1, 0),
			planOffer: planOffered,
			sized: sizedRow === null ? null : {plans: sizedPlans, chosen: sizedRow.plan, choose: setSizedChoice},
			// A placed plan order: the strip says so while the plan arrives.
			confirming: planPurchase.settling,
			startPlanChoice: purchase.startPlanChoice,
			paymentOpen: purchase.paymentOpen,
			continueWithPlan,
			plan: planPurchase,
			planPlaced,
			dismissPayment: dismissOverLimitPayment,
			retryQuote: purchase.retryQuote,
		},
		activeStorage: {
			passPaid,
			reportUpgradeClicked: storageFunnel.reportClicked,
		},
		submit, buildPayload,
	};
}
